import type { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { RemitClaim, RemitClaimPayload } from '@/types/billing';
import PaginatedResponse from '@/types/paginatedResponse';
import type RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export const remitClaimRepository = {
  async listRemitClaims({
    host,
    params
  }: {
    host: string;
    params: Record<string, string>;
  }): Promise<RepositoryResponse<PaginatedResponse<RemitClaim[]>>> {
    try {
      const path = `/api/billing/remit_claims/`;
      const response = await ajax.get({
        url: `${host}${path}`,
        params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to list Remit Claims.');
    }
  },

  async getRemitClaim({
    host,
    remitClaimID
  }: {
    host: string;
    remitClaimID: string;
  }): Promise<RepositoryResponse<RemitClaim>> {
    try {
      const path = `/api/billing/remit_claims/${remitClaimID}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get Remit Claim.');
    }
  },

  async create({ host, data }: { host: string; data: RemitClaimPayload }): Promise<RemitClaim> {
    const path = `/api/billing/remit_claims/`;
    const response = await ajax.post({
      url: `${host}${path}`,
      data
    });

    return response.data as unknown as RemitClaim;
  },

  async update({ host, data }: { host: string; data: RemitClaimPayload }): Promise<RemitClaim> {
    const path = `/api/billing/remit_claims/${data.id}/`;
    const response = await ajax.put({
      url: `${host}${path}`,
      data
    });

    return response.data as unknown as RemitClaim;
  },

  async delete({ host, id }: { host: string; id: string }): Promise<RepositoryResponse<null>> {
    try {
      const path = `/api/billing/remit_claims/${id}/`;
      await ajax.delete({
        url: `${host}${path}`
      });

      return {
        data: null,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to delete Remit Claim.');
    }
  }
};

const RemitClaimRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async listRemitClaims(
      params: Record<string, string> = {}
    ): Promise<RepositoryResponse<PaginatedResponse<RemitClaim[]>>> {
      return remitClaimRepository.listRemitClaims({ host, params });
    },

    async getRemitClaim({
      remitClaimID
    }: {
      remitClaimID: string;
    }): Promise<RepositoryResponse<RemitClaim>> {
      return remitClaimRepository.getRemitClaim({ host, remitClaimID });
    },
    async create({ data }: { data: RemitClaimPayload }): Promise<RemitClaim> {
      return remitClaimRepository.create({ host, data });
    },
    async update({ data }: { data: RemitClaimPayload }): Promise<RemitClaim> {
      return remitClaimRepository.update({ host, data });
    },
    async delete({ id }: { id: string }): Promise<RepositoryResponse<null>> {
      return remitClaimRepository.delete({ host, id });
    }
  };
};

export default RemitClaimRepository;
